import { useMutation, useQuery } from "@apollo/react-hooks";
import { Autocomplete, Button, Labelled, Tag } from "@shopify/polaris";
import { get } from "lodash";
import React from "react";
import styled from "styled-components";

import { CREATE_STORE_TYPE } from "../../graphql/mutations";
import { GET_STORE_TYPES } from "../../graphql/queries";
import { reducerFn } from "../../helper";
import useTimeout from "../../hooks/useTimeout";

function StoreTypeAutoComplete({ values, storeId, canAdd }, ref) {
  const [state, setState] = React.useReducer(reducerFn, {
    inputValue: "",
    options: [],
    selected: [],
    deselectedOptions: [],
  });

  const [filter, setFilter] = React.useReducer(reducerFn, {
    limit: 20,
    offset: 0,
    search: "",
  });

  const { data, loading, refetch } = useQuery(GET_STORE_TYPES, {
    variables: {
      filter,
    },
  });

  const [createStoreType, { loading: loadingCreate }] = useMutation(
    CREATE_STORE_TYPE,
    {
      onCompleted: async (res) => {
        const newOption = getNewStoreType(res);
        setState((prev) => {
          return {
            ...prev,
            inputValue: "",
            selected: [...prev.selected, newOption.value],
            deselectedOptions: [newOption, ...prev.deselectedOptions]
          };
        });
        handleInputClear();
        await new Promise((res) => setTimeout(res, 500))
        refetch();
      },
      onError: (err) => {},
    },
  );

  const [setDelay] = useTimeout();
  const handleInputChange = React.useCallback((inputValue) => {
    setState({ inputValue });

    setDelay(() => {
      setFilter({ search: inputValue });
    });
  }, []);

  const handleInputClear = React.useCallback(() => {
    setState({ inputValue: "" });
    setDelay(() => {
      setFilter({ search: "" });
    });
  }, []);

  const handleCreateStoreType = React.useCallback(() => {
    if (!filter.search || !storeId) return;

    createStoreType({
      variables: {
        input: {
          storeID: storeId,
          label: (filter.search || "").trim(),
        },
      },
    });
  }, [filter.search, storeId, createStoreType, setState]);

  const handleSlection = React.useCallback(
    (selected) => {
      const selectedValues = (state.options || []).filter((opt) =>
        selected.includes(opt.value),
      );

      setState((prev) => {
        const deselectedOptions = uniqueOption(
          prev.deselectedOptions,
          selectedValues,
        );

        return {
          ...prev,
          selected,
          deselectedOptions,
        };
      });
    },
    [state.options],
  );

  const handleRemove = React.useCallback(
    (value) => () => {
      const filterFn = (item) =>
        typeof item == "object" ? item.value !== value : item !== value;

      setState((prev) => {
        const selected = prev.selected.filter(filterFn);
        const deselectedOptions = prev.deselectedOptions.filter(filterFn);

        return {
          ...prev,
          selected,
          deselectedOptions,
        };
      });
    },
    [],
  );

  const setValuesRef = React.useRef(false);
  React.useEffect(() => {
    if (setValuesRef.current || !values || values.length === 0) return;
    setState((prev) => {
      const deselectedOptions = formatStoreTypes(undefined, values);
      const options = uniqueOption(prev.options, deselectedOptions);
      const selected = getStoreTypeId(values);

      return {
        ...prev,
        options,
        selected,
        deselectedOptions,
      };
    });

    setValuesRef.current = true;
  }, [values]);

  React.useEffect(() => {
    setState((prev) => {
      const options = formatStoreTypes(data);
      return {
        ...prev,
        options,
      };
    });
  }, [data]);

  React.useImperativeHandle(ref, () => ({
    getSelected: () => state.selected,
  }));

  const textField = (
    <Autocomplete.TextField
      clearButton
      value={state.inputValue}
      placeholder="Enter store type"
      onChange={handleInputChange}
      onClearButtonClick={handleInputClear}
    />
  );

  return (
    <>
      <Labelled label="Store Types" />
      <Autocomplete
        textField={textField}
        allowMultiple
        options={state.options}
        selected={state.selected}
        onSelect={handleSlection}
        loading={loading}
        emptyState={
          filter.search && canAdd ? (
            <Button
              plain
              onClick={handleCreateStoreType}
              loading={loadingCreate}
            >
              <b>Add: </b>
              {filter.search}
            </Button>
          ) : (
            <span>No items found!</span>
          )
        }
      />

      {state.deselectedOptions && state.deselectedOptions.length > 0 ? (
        <TagContainer>
          {state.deselectedOptions.map((item) => (
            <Tag
              children={item.label}
              onRemove={handleRemove(item.value)}
              key={item.value}
            />
          ))}
        </TagContainer>
      ) : null}
    </>
  );
}

export default React.forwardRef(StoreTypeAutoComplete);

function convertToOption(node) {
  return { value: node.id, label: node.label };
}

function formatStoreTypes(data, defaultValues = []) {
  const nodes = get(data, "getStoreTypes.nodes") || defaultValues || [];

  const result = new Map();
  for (let node of nodes) {
    if (!node || !node.id) continue;
    result.set(node.id, convertToOption(node));
  }

  return Array.from(result.values());
}

function getNewStoreType(data) {
  const newStore = get(data, "createStoreType");
  if (!newStore) return;

  return convertToOption(newStore);
}

function uniqueOption(options, newOptions = []) {
  const res = new Map();
  for (let opt of [...options, ...newOptions]) {
    res.set(opt.value, opt);
  }

  return Array.from(res.values());
}

function getStoreTypeId(values) {
  const res = new Set();
  for (let val of values) {
    if (!val || !val.id) continue;
    res.add(val.id);
  }

  return Array.from(res);
}

const TagContainer = styled.div`
  display: flex;
  padding-top: 0.8rem;
  flex-wrap: wrap;
  > * {
    margin-right: 0.8rem;
    margin-bottom: 0.8rem;
  }
`;
